"use client";

import { ctaElementId, ctaHref, trackCtaClick } from "@/lib/analytics";
import { cn } from "@/lib/utils";

type Variant = "primary" | "secondary" | "ghost" | "white";
type Size = "md" | "lg" | "xl";

const variants: Record<Variant, string> = {
  primary:
    "bg-brand text-white hover:bg-brand-dark",
  secondary:
    "bg-navy text-white hover:bg-ink hover:-translate-y-0.5",
  white:
    "bg-white text-navy shadow-soft hover:-translate-y-0.5",
  ghost:
    "bg-transparent text-navy ring-1 ring-line hover:bg-mist",
};

const sizes: Record<Size, string> = {
  md: "h-11 px-5 text-base sm:px-6 sm:text-[15px]",
  lg: "h-14 px-5 text-[17px] sm:px-8 sm:text-base",
  // Hero only. The hero CTA carries the page and sits next to a 60px headline,
  // where lg reads as undersized. Kept as its own size rather than growing lg,
  // which every other CTA on the site uses.
  xl: "h-16 px-6 text-lg sm:min-w-[22rem] sm:px-12 sm:text-lg",
};

// External CTA that links to the app and reports a click to first-party analytics.
// `linkId` mirrors the original site's `link_id` query param used for attribution.
export function CtaButton({
  href,
  children,
  variant = "primary",
  size = "lg",
  linkId,
  id,
  className,
}: {
  href: string;
  children: React.ReactNode;
  variant?: Variant;
  size?: Size;
  linkId?: string;
  // Anchor id, defaulted from linkId. GTM's {{CJS - CTA Click ID}} reads
  // `a[id^="cta_"]` and trigger 8 fires clicked_on_sign_up from it - which is a
  // GA4 key event and the server-side Google Ads conversion - so a CTA without a
  // `cta_` id is invisible to paid reporting. Pass an explicit id only to keep a
  // legacy name the container already matches on (cta_hero_banner,
  // cta_urgency_banner, shared with the Framer site).
  id?: string;
  className?: string;
}) {
  const url = ctaHref(href, linkId);

  return (
    <a
      href={url}
      id={id ?? ctaElementId(linkId)}
      onClick={() => trackCtaClick(linkId, href)}
      className={cn(
        "group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2",
        variants[variant],
        sizes[size],
        className,
      )}
    >
      {/* The label is wrapped, and the wrapper is load-bearing for conversion
          reporting. GTM trigger 8 is an All-Elements click trigger, so
          `{{Click URL}}` is whatever the CLICKED element's href is - it does not
          climb to an ancestor anchor (that is exactly why the container's
          {{CJS - CTA Click ID}} variable has to call closest() by hand). The
          trigger also refuses any Click URL containing `/login`, which is where
          every CTA here goes.

          With a bare text node the click target is the <a> itself, Click URL is
          populated, and the conversion is dropped. Measured on a production
          build: a click at the label's pixel reported
          gtm.elementUrl=https://app.trein-vertraging.nl/login?link_id=hero_cta.
          With a wrapping element the target is the <span>, Click URL is empty,
          and the conversion fires.

          This is also what makes the A/B arms comparable: the Framer control
          renders its CTA label inside <div><div><p>, so it has always behaved
          this way, and our bare text node was the odd one out. Do not "simplify"
          this back to {children}. */}
      <span>{children}</span>
      <svg
        className={cn(
          "size-4 transition-transform duration-200 group-hover:translate-x-0.5",
          size === "md" ? "hidden sm:block" : "block",
        )}
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M6 3l5 5-5 5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </a>
  );
}
